<template>
  <SectionContainer
      :colors="colors"
      :background-variant="backgroundVariant"
      class="w-full"
      v-if="title || contentHtml || shownAreas.length > 0"
  >
    <div class="w-full max-w-6xl mx-auto px-4 sm:px-6 flex flex-col md:flex-row gap-10 items-center">
      <div class="flex-1 min-w-0 w-full">
        <component :is="headingTag" v-if="title" class="text-3xl font-bold" style="color: var(--theme-text-heading);">
          {{ title }}
        </component>
        <div v-if="contentHtml" class="areas-content text-lg leading-relaxed mt-4 space-y-3"
             style="color: var(--theme-text-body);"
             v-html="contentHtml"></div>
        <ul v-if="shownAreas.length > 0" class="mt-6 flex flex-wrap gap-2" aria-label="Areas we cover">
          <li
              v-for="(area, index) in shownAreas"
              :key="index"
              class="areas-chip inline-flex items-center gap-1.5 rounded-full border-2 px-3 py-1.5 text-sm font-semibold"
              style="border-color: var(--brand-accent); color: var(--theme-text-heading);"
          >
            <MapPin class="w-4 h-4 shrink-0" style="color: var(--brand-accent);" aria-hidden="true"/>
            {{ area }}
          </li>
        </ul>
      </div>
      <div v-if="imageUrl && showImage" class="w-full md:w-5/12 shrink-0">
        <img
            :src="resolveMediaUrl(imageUrl)"
            :alt="title ? `Map: ${title}` : 'Areas we cover'"
            class="w-full h-auto max-h-[60vh] object-cover rounded-lg"
            loading="lazy"
            @error="showImage = false"
        />
      </div>
    </div>
  </SectionContainer>
</template>

<script setup>
import {useSectionHeadingTag} from "@/composables/useSectionHeadingTag.js";
import {computed, ref, watch} from "vue";
import {MapPin} from "@iconoir/vue";
import SectionContainer from "@/components/SectionContainer.vue";
import {resolveMediaUrl} from "@/composables/images.js";
import {safeMarkdown} from "@/utils/safeMarkdown.js";
import {shownImageUrl} from "@/utils/sectionMedia.js";
const headingTag = useSectionHeadingTag();

// "Areas we cover" (Website Studio type `areas`): {title, content, areas: [string], image?}.
const props = defineProps({
  title: {type: String, default: ""},
  content: {type: String, default: ""},
  areas: {type: Array, default: () => []},
  image: {type: Object, default: () => ({url: ""})},
  colors: {type: Object, default: () => ({})},
  backgroundVariant: {type: Number, default: 1},
});

const showImage = ref(true);
// A Pexels pick shows pexels.src.large; otherwise url (the server signs saved
// ones, a studio draft carries Tokyo's displayUrl there).
const imageUrl = computed(() => shownImageUrl(props.image));
watch(imageUrl, () => { showImage.value = true; });
const contentHtml = computed(() => safeMarkdown(props.content));
const shownAreas = computed(() =>
    (props.areas || []).filter((a) => typeof a === "string" && a.trim() !== "").map((a) => a.trim())
);
</script>

<style scoped>
/* The base layer colours every <p> gray-800; follow the theme's body text
   instead, so dark themes (noir, neon) stay readable. */
.areas-content :deep(p),
.areas-content :deep(li) {
  color: inherit;
  font-size: inherit;
  line-height: inherit;
}
.areas-content :deep(a) {
  color: var(--brand-accent);
  text-decoration: underline;
}
</style>
